import styled, { css, keyframes } from 'styled-components';
import tw, { theme } from 'twin.macro';

export type PulsingDotColor = 'blue' | 'green' | 'orange' | 'red';

/* The dot's own colour, which the halo takes too */
const dotColor: Record<PulsingDotColor, string> = {
  blue: theme`colors.blue.base`,
  green: theme`colors.green.darken-10`,
  orange: theme`colors.orange.base`,
  red: theme`colors.red.base`
};

/* The halo: it grows out of the dot and fades as it goes, then holds at that
   size for the rest of the cycle. The hold is what makes it read as a
   heartbeat rather than as a continuous ripple, and what keeps a screen full
   of dots from reading as movement across the whole of it. */
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  70% {
    transform: scale(2);
    opacity: 0;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
`;

const StyledPulsingDot = styled.span<{ $color: PulsingDotColor }>(({ $color }) => [
  tw`relative inline-block shrink-0 w-8 h-8 rounded-full align-middle`,
  css`
    background-color: ${dotColor[$color]};
    /* The halo sits on a pseudo-element rather than on the dot itself: the dot
       keeps its size and its place, so nothing beside it moves while the
       animation runs */
    &::after {
      content: '';
      ${tw`absolute inset-0 rounded-full`}
      background-color: ${dotColor[$color]};
      animation: ${pulse} 2s ease-out infinite;
    }
  `,
  css`
    @media (prefers-reduced-motion: reduce) {
      &::after {
        animation: none;
      }
    }
  `
]);

export default StyledPulsingDot;
